import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const AdminNotificationContext = createContext();

export const useAdminNotifications = () => {
    const context = useContext(AdminNotificationContext);
    if (!context) {
        throw new Error('useAdminNotifications must be used within an AdminNotificationProvider');
    }
    return context;
};

export const AdminNotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    // Fetch unread count
    const fetchUnreadCount = async () => {
        if (!user || !user.is_admin) return;

        try {
            const response = await api.get('/admin/notifications/unread-count');
            setUnreadCount(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching admin unread count:', error);
        }
    };

    // Fetch all notifications
    const fetchNotifications = async (unreadOnly = false) => {
        if (!user || !user.is_admin) return;

        setLoading(true);
        try {
            const response = await api.get('/admin/notifications', {
                params: { unread_only: unreadOnly, limit: 20 }
            });
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Error fetching admin notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            await api.put(`/admin/notifications/${notificationId}/read`);

            // Update local state
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
            );

            // Refresh unread count
            fetchUnreadCount();
        } catch (error) {
            console.error('Error marking admin notification as read:', error);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            await api.put('/admin/notifications/read-all');

            // Update local state
            setNotifications(prev =>
                prev.map(n => ({ ...n, is_read: true }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all admin notifications as read:', error);
        }
    };

    // Dismiss notification
    const dismissNotification = async (notificationId) => {
        try {
            await api.put(`/admin/notifications/${notificationId}/dismiss`);

            // Update local state
            setNotifications(prev =>
                prev.filter(n => n.id !== notificationId)
            );

            // Refresh unread count
            fetchUnreadCount();
        } catch (error) {
            console.error('Error dismissing admin notification:', error);
        }
    };

    // Dismiss all notifications
    const dismissAllNotifications = async () => {
        try {
            await api.delete('/admin/notifications/dismiss-all');

            // Update local state
            setNotifications([]);
            setUnreadCount(0);
        } catch (error) {
            console.error('Error dismissing all admin notifications:', error);
        }
    };

    // Refresh all notification data
    const refreshNotifications = () => {
        fetchUnreadCount();
        fetchNotifications();
    };

    // Initial fetch and polling
    useEffect(() => {
        if (user && user.is_admin) {
            fetchUnreadCount();
            fetchNotifications();

            // Poll for new notifications every 30 seconds
            const interval = setInterval(() => {
                fetchUnreadCount();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [user]);

    const value = {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications,
        refreshNotifications
    };

    return (
        <AdminNotificationContext.Provider value={value}>
            {children}
        </AdminNotificationContext.Provider>
    );
};
